import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  AUTH_PHASE,
  beginLogin,
  extendLoginGrace,
  getGeneration,
  isGenerationCurrent,
  isInLoginGrace,
  markAuthenticated,
  markHydrating,
  markUnauthenticated,
} from "@/lib/authSessionCoordinator";
import { resolveApiBase } from "@/lib/apiBase";
import { hydrateAuthSession, installSessionRefreshInterceptor } from "@/lib/sessionRefresh";

export const API = resolveApiBase();
// Render free-tier cold starts can exceed 30s; without a timeout the login button
// stays on "Logging in…" with no error. Same-origin /api in production; withCredentials for cookies.
export const api = axios.create({ baseURL: API, withCredentials: true, timeout: 60000 });

let interceptorsInstalled = false;

function ensureSessionInterceptors(onSessionExpired) {
  if (interceptorsInstalled) return;
  installSessionRefreshInterceptor(api, { onSessionExpired });
  interceptorsInstalled = true;
}

const AuthContext = createContext(null);

export function formatApiError(e) {
  if (e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) {
    return "The server took too long to respond. Please try again in a moment.";
  }
  const detail = e?.response?.data?.detail;
  if (detail === "Instructor access required") {
    return (
      "Instructor access required — this browser session is not signed in as an instructor. " +
      "Log out of Academy, sign in again as your instructor account, and use a different browser than the learner test login."
    );
  }
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || "").filter(Boolean).join(" ");
  return e?.message || "Something went wrong. Please try again.";
}

/** Structured auth error codes from the API (e.g. email_not_verified). */
export function apiErrorCode(e) {
  const detail = e?.response?.data?.detail;
  if (detail && typeof detail === "object" && typeof detail.code === "string") {
    return detail.code;
  }
  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [authPhase, setAuthPhaseState] = useState(AUTH_PHASE.HYDRATING);
  const sessionExpiredRef = useRef(false);
  const loginActiveRef = useRef(false);

  const syncPhase = useCallback((phase) => {
    setAuthPhaseState(phase);
  }, []);

  const handleSessionExpired = useCallback(() => {
    if (sessionExpiredRef.current || loginActiveRef.current || isInLoginGrace()) return;
    sessionExpiredRef.current = true;
    markUnauthenticated();
    syncPhase(AUTH_PHASE.UNAUTHENTICATED);
    setUser(false);
  }, [syncPhase]);

  useEffect(() => {
    ensureSessionInterceptors(handleSessionExpired);
  }, [handleSessionExpired]);

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    const gen = getGeneration();
    markHydrating();
    syncPhase(AUTH_PHASE.HYDRATING);

    (async () => {
      try {
        const { user: hydratedUser } = await hydrateAuthSession(api, {
          generationToken: gen,
          signal: ac.signal,
        });
        if (cancelled || !isGenerationCurrent(gen) || loginActiveRef.current) return;
        setUser((prev) => (prev && prev !== false ? prev : hydratedUser));
        if (hydratedUser) {
          markAuthenticated();
          syncPhase(AUTH_PHASE.AUTHENTICATED);
        } else {
          markUnauthenticated();
          syncPhase(AUTH_PHASE.UNAUTHENTICATED);
        }
      } catch (error) {
        if (cancelled || !isGenerationCurrent(gen) || loginActiveRef.current) return;
        if (error?.message === "hydrate superseded" || error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
          return;
        }
        setUser((prev) => (prev === null ? false : prev));
        markUnauthenticated();
        syncPhase(AUTH_PHASE.UNAUTHENTICATED);
      } finally {
        if (!cancelled && isGenerationCurrent(gen) && !loginActiveRef.current) {
          setAuthReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [syncPhase]);

  const login = useCallback(
    async (credentials) => {
      loginActiveRef.current = true;
      sessionExpiredRef.current = false;
      const gen = beginLogin();
      syncPhase(AUTH_PHASE.LOGGING_IN);
      setAuthReady(false);

      try {
        const { data } = await api.post("/auth/login", credentials);
        extendLoginGrace();
        const meRes = await api.get("/auth/me");
        if (!isGenerationCurrent(gen)) {
          throw new Error("login superseded");
        }
        const verifiedUser = meRes.data?.user || data.user;
        loginActiveRef.current = false;
        markAuthenticated();
        syncPhase(AUTH_PHASE.AUTHENTICATED);
        sessionExpiredRef.current = false;
        setUser(verifiedUser);
        setAuthReady(true);
        return verifiedUser;
      } catch (error) {
        loginActiveRef.current = false;
        if (!isGenerationCurrent(gen)) throw error;
        markUnauthenticated();
        syncPhase(AUTH_PHASE.UNAUTHENTICATED);
        setAuthReady(true);
        throw error;
      }
    },
    [syncPhase]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    sessionExpiredRef.current = false;
    loginActiveRef.current = false;
    markUnauthenticated();
    syncPhase(AUTH_PHASE.UNAUTHENTICATED);
    setUser(false);
  }, [syncPhase]);

  const completeLogin = useCallback(
    (nextUser) => {
      sessionExpiredRef.current = false;
      loginActiveRef.current = false;
      markAuthenticated();
      syncPhase(AUTH_PHASE.AUTHENTICATED);
      setUser(nextUser);
      setAuthReady(true);
    },
    [syncPhase]
  );

  return (
    <AuthContext.Provider value={{ user, setUser: completeLogin, authReady, authPhase, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
